import React, { useEffect, useRef } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Container } from 'reactstrap';
import cn from 'classnames';
import _get from 'lodash/get';

import { If } from 'utils';
import { TourGuides } from 'containers';
import LayoutWrapper from './LayoutWrapper';
import {
  ChangePassword,
  ErrorBoundary,
  Announcement,
  TermsAndConditions,
  NotificationsBanner
} from 'components';
import { OnlineCheck } from 'components/Common';
import AppLevelAlert from 'components/AppLevelAlert';
import ShopifySubscriptionModal from 'components/SubscriptionModal';
import Frame from './Frame';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import AccountSettings from 'views/AccountSettings';
// import QuotaExhaustAlert from 'components/QuotaExhaustAlert';
// import WarehouseSetupAlert from 'components/WarehouseSetupAlert';
// import ScheduledMaintenanceAlert from 'components/ScheduledMaintenanceAlert';
import { NotificationsProvider } from 'context/NotificationsContext';
import { StoreConnectProvider } from 'context/StoreConnectContext';
import { useAppContext } from 'utils/hooks';
import ADMIN_ROUTES from 'config/nav/admin/routes';
import { IS_ALL_SSR } from 'dictionary';
import { RETEX_APP_ROUTES, TRACKING_APP_ROUTES } from 'config/routes/global';

export default function DefaultLayout({ roles, appType, configuration }) {
  const { isSSREnv } = useAppContext();

  useEffect(() => {
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, []);

  const appRoutes = useRef(
    appType.shipmentTracking ? TRACKING_APP_ROUTES : RETEX_APP_ROUTES
  ).current;

  const redirectionRoutes = () => {
    // When REACT_APP_IS_ALL_SSR is true, always redirect to configure
    if (IS_ALL_SSR) {
      return <Redirect exact from="/" to={ADMIN_ROUTES.CONFIGURE.ROOT} />;
    }

    const defaultRouteData = appRoutes.find(({ id, role, hideInSSR }) => {
      const isDefaultRole =
        role === roles.default && (!isSSREnv || (isSSREnv && !hideInSSR));
      const hasAccess = _get(configuration, id, false);
      return isDefaultRole && hasAccess;
    });

    const defaultRoute = _get(defaultRouteData, 'path', null);

    return defaultRoute ? <Redirect exact from="/" to={defaultRoute} /> : null;
  };

  const setupRoutes = () => {
    const mainRoutes = appRoutes.reduce((acc, route) => {
      if (appType.retex && (!route.component || !roles[route.role])) {
        return acc;
      } else if (appType.shipmentTracking && !route.component) {
        return acc;
      }

      // Always enable configure route when REACT_APP_IS_ALL_SSR is true
      const isConfigureRoute = route.id === 'admin.submenu.configure.enabled';
      const hasAccess = !!_get(configuration, route.id, false);
      const isRouteEnabled = (IS_ALL_SSR && isConfigureRoute) || hasAccess;
      const isVisibleInSSR = !isSSREnv || (isSSREnv && !route.hideInSSR);

      const isAllowed = isRouteEnabled && isVisibleInSSR;

      if (isAllowed) {
        acc.push(
          <Route
            key={route.name}
            path={route.path}
            name={route.name}
            role={route.role}
            exact={!!route.exact}
            render={props => <route.component {...props} />}
          />
        );
      }
      return acc;
    }, []);
    const redirection = redirectionRoutes();

    const accountSettingsRoute = isSSREnv ? null : (
      <Route
        exact
        key="account-settings-route"
        path={ADMIN_ROUTES.ACCOUNT_SETTINGS.ROOT}
        component={AccountSettings}
      />
    );

    return (
      <Switch>
        {mainRoutes}
        {accountSettingsRoute}
        {redirection}
        <Route exact path="/404" component={ErrorBoundary.NotFound} />
        <Redirect from="*" to="/404" />;
      </Switch>
    );
  };

  const containerClassName = cn('default-layout-container', {
    retexAppContainer: appType.retex,
    trackingAppContainer: appType.shipmentTracking,
    ssrAppContainer: isSSREnv
  });

  return (
    <ErrorBoundary>
      <NotificationsProvider>
        <TourGuides.WelcomeTour>
          <StoreConnectProvider>
            <Frame>
              <If test={!IS_ALL_SSR}>
                <Header />
              </If>
              <LayoutWrapper>
                <If test={!IS_ALL_SSR}>
                  <Sidebar />
                </If>
                <div className="app-body default-layout">
                  <OnlineCheck />
                  <If test={!isSSREnv}>
                    <AppLevelAlert />
                  </If>
                  {/* <QuotaExhaustAlert /> */}
                  {/* <WarehouseSetupAlert /> */}
                  {/* <ScheduledMaintenanceAlert /> */}
                  <Announcement.Container />
                  <main className="main">
                    <If test={!isSSREnv}>
                      <NotificationsBanner.Container />
                    </If>
                    <Container fluid className={containerClassName}>
                      <ToastContainer className="app-toast-container" />
                      {setupRoutes()}
                    </Container>
                    <If test={!isSSREnv}>
                      <Footer />
                    </If>
                  </main>
                  <ChangePassword.Modal />
                  <ShopifySubscriptionModal />
                  <TermsAndConditions.Modal />
                </div>
              </LayoutWrapper>
            </Frame>
          </StoreConnectProvider>
        </TourGuides.WelcomeTour>
      </NotificationsProvider>
    </ErrorBoundary>
  );
}
